import chunk from 'lodash/chunk';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchTasksMutation } from '~/redux/modules/tic/api-slice/GENERATED_ARTIFACT';
import { getTicTasks } from '~redux/modules/tic/tasks/tic.tasks.slice';
import { getContextualTicAccounts } from '~redux/modules/tic/tic.auth.selectors';

const BATCH_SIZE = 250;

/**
 * Hook to fetch linked resources that are missing from the Redux cache.
 * Uses searchTasks POST endpoint to batch fetch missing resources by ID.
 * @param resourceIds - Array of resource IDs to ensure are loaded
 *
 * Returns `attemptedIdentifiers`: the set of IDs/URIs for which a fetch has
 * been completed (whether or not TIC returned the resource). Callers can use
 * this to stop showing a loading state when TIC has confirmed the resource is
 * not accessible (e.g. Jira connection without reference_tasks, removed project
 * subscription, or assignee restriction).
 */
type Props = { resourceIds: readonly string[] } | { resourceUris: readonly string[] };
export const useFetchMissingLinkedResources = (props: Props) => {
  const { workspace } = useSelector(getContextualTicAccounts);
  const allTasks = useSelector(getTicTasks);
  const [searchTasks] = useSearchTasksMutation();

  // Track which IDs we've already requested to avoid duplicate fetches
  const requestedIdsRef = useRef<Set<string>>(new Set());
  const requestedUrisRef = useRef<Set<string>>(new Set());

  // Track identifiers where a fetch attempt has settled (found or confirmed missing).
  // Exposed to callers so they can stop showing loading state for unresolvable resources.
  const [attemptedIdentifiers, setAttemptedIdentifiers] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  // Only check for missing IDs after initial load has completed
  const hasLoadedTasks = allTasks !== null;
  let key: 'ids' | 'uris' = 'ids';
  if ('resourceUris' in props) {
    key = 'uris';
  }
  const missingIdentifiers = useMemo(() => {
    if (!hasLoadedTasks) return [];
    if ('resourceUris' in props) {
      if (!props.resourceUris.length) return [];
      const cachedUris = new Set(allTasks.map(t => t.uri));
      return props.resourceUris.filter(
        uri => !cachedUris.has(uri) && !requestedUrisRef.current.has(uri)
      );
    } else {
      if (!props.resourceIds.length) return [];
      const cachedIds = new Set(allTasks.map(t => t.id));
      return props.resourceIds.filter(id => !cachedIds.has(id) && !requestedIdsRef.current.has(id));
    }
  }, [props, allTasks, hasLoadedTasks]);

  // Fetch missing resources using the POST endpoint
  useEffect(() => {
    if (!workspace?.id || missingIdentifiers.length === 0) return;

    // Mark these IDs as requested
    if (key === 'uris') {
      missingIdentifiers.forEach(uri => requestedUrisRef.current.add(uri));
    } else {
      missingIdentifiers.forEach(id => requestedIdsRef.current.add(id));
    }

    Promise.all(
      chunk(missingIdentifiers, BATCH_SIZE).map(batch =>
        searchTasks({
          body: {
            accountId: workspace.id,
            [key]: batch,
            limit: batch.length,
          },
        })
      )
    )
      .then(() => {
        // Fetch settled (tasks found or confirmed not accessible) — mark as attempted
        // so callers can stop showing a loading state for these identifiers.
        setAttemptedIdentifiers(prev => {
          const next = new Set(prev);
          missingIdentifiers.forEach(id => next.add(id));
          return next;
        });
      })
      .catch(() => {
        // Keep identifiers in requestedIdsRef/requestedUrisRef so they are not retried
        // (removing them would cause an infinite loop: re-render → memo re-includes them → re-fetch).
        // Still mark as attempted so callers clear the loading state immediately.
        // Do not re-throw — the promise is fire-and-forget inside a useEffect, so re-throwing
        // would produce an unhandled rejection in error monitoring with no benefit.
        setAttemptedIdentifiers(prev => {
          const next = new Set(prev);
          missingIdentifiers.forEach(id => next.add(id));
          return next;
        });
      });
  }, [workspace?.id, missingIdentifiers, key, searchTasks]);

  return { attemptedIdentifiers };
};
